"use client"

import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { addArticleToCurrentBasketAction } from "@/actions/basketActions"
import { loadCardPricesForProductAction } from "@/actions/priceActions"
import { LoaderCircle, MinusIcon, PlusIcon, ShoppingCartIcon, TrashIcon } from "lucide-react"

import { formatPrice } from "@/lib/helper"
import { CardPrices } from "@/lib/price-utils"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { trackAddToBasket } from "@/lib/tracking/tracking-helper"
import { Button } from "@/components/ui/components"
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/components/sheet"
import { cn } from "@/components/ui/helpers/utils"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"
import { UbAccountOrderViewStoryblok } from "@/components/storyblok/component-types"

interface OrderItem {
  id: string
  title?: string
  brand?: string
  quantity: number
  imageUrl?: string
  article?: ReducedProductData
}

interface ReorderOverlayProps {
  lineItems?: OrderLineItemData[]
  isoLocale: string
  customerId: string | undefined
  isB2B: boolean
  blok?: UbAccountOrderViewStoryblok
}

interface ReorderOverlayItemProps {
  item: OrderItem
  customerId: string | undefined
  isoLocale: string
  isB2B: boolean
  onItemChange: (item: OrderItem | null) => void
  blok?: UbAccountOrderViewStoryblok
}

function ReorderOverlayProductItem({
  item,
  isoLocale,
  isB2B,
  customerId,
  onItemChange,
  blok,
}: ReorderOverlayItemProps) {
  const [cardPrices, setCardPrices] = React.useState<CardPrices | undefined>(undefined)
  const [localItem, setLocalItem] = useState<OrderItem>(item)

  const increment = () => {
    const updated = { ...localItem, quantity: localItem.quantity + 1 }
    setLocalItem(updated)
    onItemChange(updated.quantity > 0 ? updated : null)
  }

  const decrement = () => {
    const newQuantity = Math.max(0, localItem.quantity - 1)
    const updated = { ...localItem, quantity: newQuantity }
    setLocalItem(updated)
    onItemChange(updated.quantity > 0 ? updated : null)
  }

  const updateQuantity = (value: string) => {
    const quantity = Math.max(0, parseInt(value) || 0)
    const updated = { ...localItem, quantity }
    setLocalItem(updated)
    onItemChange(updated.quantity > 0 ? updated : null)
  }

  useEffect(() => {
    loadCardPricesForProductAction(isoLocale, isB2B, item.article?.ecoTax, item.article?.id ?? "", customerId).then(
      (loadedCardPrices) => {
        if (loadedCardPrices) {
          //console.dir(loadedCardPrices)
          setCardPrices(loadedCardPrices)
        }
      }
    )
  }, [customerId, item, isB2B, isoLocale])

  return (
    <div className="flex flex-col justify-between gap-2 rounded-sm border bg-white p-4">
      {/* Left Side: Image, Info, Quantity Controls */}
      <div className="flex flex-1 items-start gap-4">
        <ImageWithFallback
          className={"aspect-square h-20 object-contain"}
          src={localItem.imageUrl ?? ""}
          width={80}
          height={80}
          alt={localItem.title ?? ""}
        />
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-600">{localItem.brand}</p>
            <p className="text-sm font-medium">{localItem.title}</p>
          </div>
          <div className={"flex flex-row gap-2"}>
            <div className={cn("flex w-auto flex-row items-center justify-center rounded-lg bg-gray-200 p-0 px-2")}>
              <button
                className={cn(
                  "w-full rounded-t-lg py-0.5 max-lg:flex max-lg:justify-center lg:w-auto lg:rounded-full lg:py-1 lg:pl-4 lg:pr-2"
                )}
                onClick={decrement}
                aria-label={blok?.label_product_quantity_decrease}
              >
                <MinusIcon className={cn("size-5")} />
              </button>
              <input
                type="text"
                pattern="[0-9]*"
                inputMode={"numeric"}
                aria-label={blok?.label_product_quantity}
                className={cn(
                  "h-9 w-12 border-transparent bg-transparent p-0 py-1 text-center text-base font-bold focus:rounded-md focus:border-2 focus:border-blue-400 focus:outline-none focus:ring-transparent lg:text-lg"
                )}
                value={localItem.quantity}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuantity(e.target.value)}
                onKeyDown={(event) => {
                  if (
                    !(
                      /[0-9]/.test(event.key) ||
                      event.code === "Backspace" ||
                      event.code === "Delete" ||
                      event.code === "Delete" ||
                      event.code === "ArrowLeft" ||
                      event.code === "ArrowRight"
                    )
                  ) {
                    event.preventDefault()
                  }
                }}
              />
              <button
                className={cn(
                  "w-full rounded-b-lg py-0.5 max-lg:flex max-lg:justify-center lg:w-auto lg:rounded-full lg:py-1  lg:pl-4 lg:pr-2"
                )}
                onClick={increment}
                aria-label={blok?.label_product_quantity_increase}
              >
                <PlusIcon className={cn("size-5")} />
              </button>
            </div>
            <div className={"flex items-center justify-center"}>
              <Button onClick={() => onItemChange(null)} variant={"destructive"} size={"iconNoGrow"}>
                <TrashIcon className={"size-5"} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Vertical divider */}
      <div className="mx-4 w-px bg-gray-300" />

      {/* Right Side: Price info */}
      <div className="flex flex-col items-end justify-between whitespace-nowrap text-sm">
        <div className="text-gray-500">
          {isB2B ? blok?.label_price_unit_price_excluding_vat : blok?.label_price_unit_price_including_vat}
        </div>

        <div className="font-semibold">{formatPrice(isoLocale, cardPrices?.salePrice, cardPrices?.currency)}</div>
        <div className="mt-2 text-gray-500">
          {isB2B ? blok?.label_price_unit_price_net_amount : blok?.label_price_unit_price_gross_amount}
        </div>
        <div className="font-bold">
          {formatPrice(isoLocale, (cardPrices?.salePrice || 0) * localItem.quantity, cardPrices?.currency)}
        </div>
      </div>
    </div>
  )
}

export default function ReorderOverlay({ lineItems, isoLocale, isB2B, customerId, blok }: ReorderOverlayProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const router = useRouter()

  const itemsToAdd: OrderItem[] | undefined = useMemo(
    () =>
      lineItems?.map((li) => ({
        id: li.product?.id ?? "",
        title: li.product?.title ?? "",
        brand: li.product?.brand ?? "",
        unitPrice: li.orderLineItem?.pricing?.price?.net ?? 0,
        quantity: li.orderLineItem.quantity.value ?? 0,
        imageUrl: li.product?.image ?? "",
        article: li.product,
      })),
    [lineItems]
  )
  const [items, setItems] = useState<OrderItem[] | undefined>(itemsToAdd)

  async function addAllToCart() {
    if (!items) return

    setLoading(true)
    try {
      await Promise.all(
        items.map((item) =>
          addArticleToCurrentBasketAction(item.id, item.quantity).then((res) =>
            trackAddToBasket(res.isoLocale, res.isB2B, res.product, res.prices, res.quantity)
          )
        )
      )
      setOpen(false)
      setLoading(false)
      router.push("/basket/overview")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button size={"smNoGrow"} variant={"outline"} className="bg-primary text-white">
            <ShoppingCartIcon strokeWidth={1} className="inline" />
            <span className="ml-2">{blok?.label_button_buy_again}</span>
          </Button>
        </SheetTrigger>
        <SheetContent side={"right"} className={"bg-ub-background"}>
          <SheetTitle className={"hidden"}>{blok?.label_button_buy_again}</SheetTitle>
          <div className="grid size-full grid-cols-1 grid-rows-[max-content_auto_max-content] gap-6">
            <h2 className="text-2xl font-bold">{blok?.label_button_buy_again}</h2>
            <div className="grid auto-rows-max grid-cols-1 gap-4 overflow-y-auto">
              {items?.map((item) => (
                <ReorderOverlayProductItem
                  key={item.id}
                  item={item}
                  isoLocale={isoLocale}
                  isB2B={isB2B}
                  blok={blok}
                  customerId={customerId}
                  onItemChange={(updated) => {
                    setItems((prev) => {
                      if (!updated) return prev?.filter((i) => i.id !== item.id)
                      return prev?.map((i) => (i.id === updated.id ? updated : i))
                    })
                  }}
                />
              ))}
            </div>

            <div className="flex justify-end">
              <Button onClick={addAllToCart} disabled={loading}>
                {loading && <LoaderCircle className={"size-5 motion-safe:animate-spin"} />}
                {loading ? blok?.label_product_is_adding : blok?.label_button_add_to_basket}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
